import React, { useState } from 'react'

import ChangeRow from './ChangeRow'
import LoadingSpinner from '../UI/LoadingSpinner'
import StatusMessage from '../UI/StatusMessage'
import { useFlyerUpdate } from '../../hooks/useFlyerUpdate'

/**
 * Upload a newer flyer for an existing event, review what it changes, apply
 * the parts you accept.
 *
 * Three states in one dialog — pick a file, read the diff, confirm — because
 * the middle state only makes sense next to the flyer it came from.
 */
const FlyerUpdateModal = ({ isOpen, eventId, onClose, onApplied }) => {
  const [isDragOver, setIsDragOver] = useState(false)

  const {
    file,
    preview,
    changes,
    accepted,
    extracting,
    applying,
    error,
    selectFile,
    extract,
    toggleChange,
    setAllAccepted,
    apply,
    reset
  } = useFlyerUpdate(eventId, onApplied)

  if (!isOpen) return null

  const close = () => {
    reset()
    onClose()
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setIsDragOver(false)
    const dropped = e.dataTransfer.files?.[0]
    if (dropped) selectFile(dropped)
  }

  const removals = changes ? changes.filter(c => c.type === 'artist-removed') : []
  const additions = changes ? changes.filter(c => c.type === 'artist-added') : []
  const others = changes ? changes.filter(c => c.type !== 'artist-removed' && c.type !== 'artist-added') : []

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900/95 border border-white/20 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-6 pb-4 border-b border-white/10">
          <h3 className="text-lg font-semibold text-white">📄 Update from flyer</h3>
          <button
            onClick={close}
            className="text-gray-400 hover:text-white transition-colors text-xl leading-none"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          <StatusMessage type="error" message={error} />

          {/* Step 1 — choose a flyer */}
          {!changes && (
            <div className="space-y-4">
              <p className="text-sm text-gray-300">
                Got a flyer with updated info — added artists, set times, a new date?
                Upload it and you'll see exactly what changes before anything is saved.
              </p>

              <div
                className={`border-2 border-dashed rounded-xl p-6 text-center transition-all duration-200 ${
                  isDragOver ? 'border-purple-400 bg-purple-400/10' : 'border-white/30 hover:border-white/50'
                }`}
                onDragOver={(e) => { e.preventDefault(); setIsDragOver(true) }}
                onDragLeave={() => setIsDragOver(false)}
                onDrop={handleDrop}
              >
                {file ? (
                  <div className="space-y-4">
                    <div className="text-green-400 font-medium">✓ {file.name}</div>
                    {preview && (
                      <img
                        src={preview}
                        alt="Updated flyer"
                        className="max-h-56 mx-auto rounded-lg shadow-lg"
                      />
                    )}
                    <div className="flex gap-3 justify-center">
                      <button
                        type="button"
                        onClick={extract}
                        disabled={extracting}
                        className="bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 text-white px-4 py-2 rounded-lg font-medium transition-all duration-200"
                      >
                        {extracting ? <LoadingSpinner text="Reading flyer…" /> : '🔍 See what changed'}
                      </button>
                      <button
                        type="button"
                        onClick={reset}
                        className="bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-lg font-medium transition-all duration-200"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="text-4xl mb-3">📄</div>
                    <p className="text-white font-medium mb-1">Drop the updated flyer here</p>
                    <p className="text-sm text-gray-400 mb-4">JPG, PNG, or PDF</p>
                    <input
                      type="file"
                      accept=".jpg,.jpeg,.png,.pdf"
                      onChange={(e) => selectFile(e.target.files?.[0])}
                      className="hidden"
                      id="flyerUpdateInput"
                    />
                    <label
                      htmlFor="flyerUpdateInput"
                      className="inline-block bg-white/10 hover:bg-white/20 text-white font-medium py-2 px-4 rounded-lg border border-white/20 hover:border-white/40 transition-all duration-200 cursor-pointer"
                    >
                      📁 Browse files
                    </label>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 2 — review the diff */}
          {changes && changes.length === 0 && (
            <div className="text-center py-8">
              <div className="text-4xl mb-3">✅</div>
              <h4 className="text-white font-medium mb-1">Nothing new on this flyer</h4>
              <p className="text-sm text-gray-400">
                Everything it shows already matches this event.
              </p>
            </div>
          )}

          {changes && changes.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <p className="text-sm text-gray-300">
                  {changes.length} {changes.length === 1 ? 'change' : 'changes'} detected ·{' '}
                  <span className="text-white">{accepted.size} selected</span>
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setAllAccepted(true)}
                    className="text-xs bg-white/10 hover:bg-white/20 text-white px-3 py-1.5 rounded-lg transition-colors"
                  >
                    Select all
                  </button>
                  <button
                    type="button"
                    onClick={() => setAllAccepted(false)}
                    className="text-xs bg-white/10 hover:bg-white/20 text-white px-3 py-1.5 rounded-lg transition-colors"
                  >
                    Clear
                  </button>
                </div>
              </div>

              {others.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-gray-400 uppercase tracking-wide">Event details</h4>
                  {others.map(change => (
                    <ChangeRow
                      key={change.id}
                      change={change}
                      checked={accepted.has(change.id)}
                      onToggle={toggleChange}
                    />
                  ))}
                </div>
              )}

              {additions.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-gray-400 uppercase tracking-wide">
                    Added to the lineup ({additions.length})
                  </h4>
                  {additions.map(change => (
                    <ChangeRow
                      key={change.id}
                      change={change}
                      checked={accepted.has(change.id)}
                      onToggle={toggleChange}
                    />
                  ))}
                </div>
              )}

              {removals.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-red-300 uppercase tracking-wide">
                    Missing from this flyer ({removals.length})
                  </h4>
                  <p className="text-xs text-gray-400">
                    Left unchecked on purpose — a flyer the AI read only partly can look
                    like artists were dropped. Check these only if they really are off the bill.
                  </p>
                  {removals.map(change => (
                    <ChangeRow
                      key={change.id}
                      change={change}
                      checked={accepted.has(change.id)}
                      onToggle={toggleChange}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {changes && changes.length > 0 && (
          <div className="flex gap-3 justify-end p-6 pt-4 border-t border-white/10">
            <button
              type="button"
              onClick={close}
              className="bg-white/10 hover:bg-white/20 text-white font-medium py-2 px-4 rounded-lg transition-all duration-200"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={apply}
              disabled={applying || accepted.size === 0}
              className="bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-medium py-2 px-5 rounded-lg transition-all duration-200"
            >
              {applying
                ? <LoadingSpinner text="Applying…" />
                : `Apply ${accepted.size} ${accepted.size === 1 ? 'change' : 'changes'}`}
            </button>
          </div>
        )}

        {changes && changes.length === 0 && (
          <div className="flex justify-end p-6 pt-4 border-t border-white/10">
            <button
              type="button"
              onClick={close}
              className="bg-purple-600 hover:bg-purple-700 text-white font-medium py-2 px-5 rounded-lg transition-all duration-200"
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default FlyerUpdateModal
